/**
 * ✅ FASE 10: Performance Monitor Service
 * Armazena e analisa métricas de performance em tempo real
 */

import { supabase } from '@/integrations/supabase/client';

interface MetricRecord {
  name: string;
  value: number;
  rating: 'good' | 'needs-improvement' | 'poor';
  timestamp: number;
}

interface NavigationRecord {
  domContentLoaded: number;
  loadComplete: number;
  transferSize: number;
  type: string;
}

interface PerformanceStats {
  metrics: MetricRecord[];
  navigation: NavigationRecord | null;
  averages: {
    FCP: number;
    LCP: number;
    CLS: number;
    TTFB: number;
    INP: number;
  };
  sessionStart: number;
  lastUpdate: number;
}

class PerformanceMonitorService {
  private stats: PerformanceStats;
  private listeners: Set<(stats: PerformanceStats) => void>;
  private readonly MAX_METRICS = 100; // Limitar memória

  constructor() {
    this.stats = {
      metrics: [],
      navigation: null,
      averages: {
        FCP: 0,
        LCP: 0,
        CLS: 0,
        TTFB: 0,
        INP: 0,
      },
      sessionStart: Date.now(),
      lastUpdate: Date.now(),
    };
    this.listeners = new Set();
  }

  async recordMetric(metric: MetricRecord) {
    // Adicionar métrica
    this.stats.metrics.push(metric);

    // Limitar tamanho do array
    if (this.stats.metrics.length > this.MAX_METRICS) {
      this.stats.metrics.shift();
    }

    // Recalcular médias
    this.calculateAverages();

    // Atualizar timestamp
    this.stats.lastUpdate = Date.now();

    // Notificar listeners
    this.notifyListeners();

    // Persistir no banco de dados (fire and forget)
    this.persistMetric(metric).catch(error => {
      console.warn('Failed to persist performance metric:', error);
    });
  }

  private async persistMetric(metric: MetricRecord) {
    try {
      // Get user ID
      const { data: { user } } = await supabase.auth.getUser();
      const userId = user?.id || null;

      // Store in database
      await supabase.from('performance_metrics').insert({
        metric_name: metric.name,
        metric_value: metric.value,
        rating: metric.rating,
        user_id: userId,
        url: window.location.href,
      });

      // Log in development
      if (process.env.NODE_ENV === 'development') {
        console.log('📊 Performance Metric persisted:', {
          name: metric.name,
          value: metric.value,
          rating: metric.rating,
        });
      }
    } catch (error) {
      // Silent fail - não queremos quebrar a aplicação por falha em logging
      if (process.env.NODE_ENV === 'development') {
        console.warn('Failed to persist metric:', error);
      }
    }
  }

  recordNavigation(navigation: NavigationRecord) {
    this.stats.navigation = navigation;
    this.notifyListeners();
  }

  private calculateAverages() {
    const metricsByName = this.groupMetricsByName();
    
    Object.keys(this.stats.averages).forEach((name) => {
      const values = metricsByName[name] || [];
      if (values.length > 0) {
        this.stats.averages[name as keyof typeof this.stats.averages] = 
          values.reduce((sum, v) => sum + v, 0) / values.length;
      }
    });
  }

  private groupMetricsByName(): Record<string, number[]> {
    return this.stats.metrics.reduce((acc, metric) => {
      if (!acc[metric.name]) {
        acc[metric.name] = [];
      }
      acc[metric.name].push(metric.value);
      return acc;
    }, {} as Record<string, number[]>);
  }

  subscribe(listener: (stats: PerformanceStats) => void) {
    this.listeners.add(listener);
    // Enviar estado atual imediatamente
    listener(this.stats);
    
    return () => {
      this.listeners.delete(listener);
    };
  }

  private notifyListeners() {
    this.listeners.forEach(listener => listener(this.stats));
  }

  getStats(): PerformanceStats {
    return this.stats;
  }

  getPerformanceScore(): number {
    const { averages } = this.stats;
    
    // Critérios do Lighthouse para "good" performance
    const scores = {
      FCP: this.scoreMetric(averages.FCP, 1800, 3000), // < 1.8s = good, > 3s = poor
      LCP: this.scoreMetric(averages.LCP, 2500, 4000), // < 2.5s = good, > 4s = poor
      CLS: this.scoreMetric(averages.CLS, 0.1, 0.25, true), // < 0.1 = good, > 0.25 = poor (inverter)
      TTFB: this.scoreMetric(averages.TTFB, 800, 1800), // < 0.8s = good, > 1.8s = poor
      INP: this.scoreMetric(averages.INP, 200, 500), // < 200ms = good, > 500ms = poor
    };

    // Média ponderada (LCP e INP são mais importantes)
    const weightedScore = 
      (scores.FCP * 0.15) +
      (scores.LCP * 0.30) +
      (scores.CLS * 0.15) +
      (scores.TTFB * 0.10) +
      (scores.INP * 0.30);

    return Math.round(weightedScore);
  }

  private scoreMetric(value: number, goodThreshold: number, poorThreshold: number, inverse = false): number {
    if (value === 0) return 0;
    
    if (inverse) {
      // Para CLS, menor é melhor
      if (value <= goodThreshold) return 100;
      if (value >= poorThreshold) return 0;
      return Math.round(100 - ((value - goodThreshold) / (poorThreshold - goodThreshold)) * 100);
    } else {
      // Para outras métricas, menor é melhor
      if (value <= goodThreshold) return 100;
      if (value >= poorThreshold) return 0;
      return Math.round(100 - ((value - goodThreshold) / (poorThreshold - goodThreshold)) * 100);
    }
  }

  reset() {
    this.stats.metrics = [];
    this.stats.navigation = null;
    this.stats.averages = {
      FCP: 0,
      LCP: 0,
      CLS: 0,
      TTFB: 0,
      INP: 0,
    };
    this.stats.sessionStart = Date.now();
    this.stats.lastUpdate = Date.now();
    this.notifyListeners();
  }
}

export const performanceMonitorService = new PerformanceMonitorService();
